import { FormBuilder, FormArray } from "@angular/forms";
import { FieldFactory, ParentFieldFactory, CreateFormContextOptions } from "../field-factory";
import { DynamicArrayController } from "./dynamic-array-controller";
import { ArrayFormFieldSetting } from "./array-form-field-setting";
import { FormContext } from "../../form/form-context";
import { FormRootDirective } from "../../form/form-root.directive";
import { FormFieldArrayComponent } from "./form-field-array-component";
import { ViewRef } from "@angular/core";
import { ArrayRenderer } from "./array-renderer";

export class ArrayFieldFactory implements FieldFactory<FormArray, ArrayFormFieldSetting<any, any>> {
  constructor(private parentFieldFactory: ParentFieldFactory, private formBuilder: FormBuilder) {}

  public create<M>(
    options: CreateFormContextOptions<M, FormArray, ArrayFormFieldSetting<M>>
  ): FormContext<M, FormArray> {
    const { setting } = options;

    if (!setting.childSetting) {
      throw new Error(`Array ${options.setting.name} needs 'childSetting' as part of its setting.`);
    }

    const [formControl, children] = this.createChildContexts(options);

    const dynamicController = new DynamicArrayController(this.parentFieldFactory);

    const arrayComponentContext = {
      ...this.parentFieldFactory.resolveComponent<M, FormArray>(
        formControl,
        setting,
        options.initialValue,
        dynamicController
      ),
      children
    };

    const arrayComponent = arrayComponentContext.componentRef.instance as FormFieldArrayComponent;

    if (!arrayComponent.formRoot) {
      throw new Error(
        `Array component ${setting.name} is not an array component. It is missing the 'formRoot' directive.`
      );
    }

    // initialize controller after all values and children are resolved
    dynamicController.onInit(arrayComponentContext);

    return arrayComponentContext;
  }

  public updateChildren(arrayContext: FormContext<any, FormArray>) {
    const unchecked = [...arrayContext.formControl.controls];
    (arrayContext.children as FormContext<any>[]).forEach((child, i) => {
      if (arrayContext.formControl.controls.indexOf(child.formControl) === -1) {
        arrayContext.formControl.insert(i, child.formControl);
      } else {
        unchecked.splice(unchecked.indexOf(child.formControl), 1);
      }
    });

    unchecked.forEach(control => arrayContext.formControl.removeAt(arrayContext.formControl.controls.indexOf(control)));
  }

  public render(formRoot: FormRootDirective, arrayContext: FormContext<any, FormArray>): ViewRef {
    const viewRef = formRoot.viewContainerRef.insert(arrayContext.componentRef.hostView);
    viewRef.detectChanges();

    const arrayComponent = arrayContext.componentRef.instance as FormFieldArrayComponent;
    const paginator = (arrayContext.setting as ArrayFormFieldSetting).renderStrategy.paginator;
    const pageEvent = { pageIndex: paginator.pageIndex, pageSize: paginator.pageSize };

    const renderer = new ArrayRenderer(this.parentFieldFactory, arrayComponent.formRoot);
    renderer.render(arrayContext, pageEvent);

    return viewRef;
  }

  private createChildContexts<M>(
    options: CreateFormContextOptions<M, FormArray, ArrayFormFieldSetting<M>>
  ): [FormArray, FormContext<M>[]] {
    if (options.formControl) {
      const children = options.formControl.controls.map(formControl =>
        this.parentFieldFactory.create<any>({
          formControl,
          setting: options.setting.childSetting,
          settings: options.settings
        })
      );

      return [options.formControl, children];
    }

    const values = options.initialValue ? options.initialValue : [];

    if (!Array.isArray(values)) {
      throw new Error(`Value of ${options.setting.name} is not an array.`);
    }

    const children = values.map(value =>
      this.parentFieldFactory.create<any>({
        initialValue: value,
        setting: options.setting.childSetting,
        settings: options.settings
      })
    );

    const formControl = this.formBuilder.array(children.map(context => context.formControl));

    return [formControl, children];
  }
}
